import { Controller } from "@hotwired/stimulus";

// Left slide-out drawer for the admin layout's global nav on small screens
// (TASK-162). Mirrors the app sidebar's overlay mechanic — 300ms slide,
// dimmed backdrop, tap-outside/Escape closes — without its desktop
// pin/expand state: below `lg` the one rail DOM is an off-canvas drawer (inert
// while closed); from `lg` it is the static rail and is never inert. Nothing
// is persisted. The topbar hamburger and the drawer share this controller's
// scope via the layout wrapper, which also closes on turbo:before-cache so
// a Back-button snapshot never restores a stuck-open drawer.
export default class extends Controller {
  static targets = ["drawer", "backdrop", "toggle", "openIcon", "closeIcon"];

  connect() {
    this.mediaQuery = window.matchMedia("(min-width: 1024px)");
    this.boundMedia = () => this._syncInert();
    this.mediaQuery.addEventListener("change", this.boundMedia);
    this._syncInert();
  }

  disconnect() {
    this.mediaQuery?.removeEventListener("change", this.boundMedia);
  }

  // Closed drawer links stay out of the tab order below lg; the desktop rail
  // is always live.
  _syncInert() {
    if (!this.hasDrawerTarget) return;
    this.drawerTarget.inert = !this.mediaQuery.matches && !this._isOpen;
  }

  toggle() {
    this._isOpen ? this.close() : this.open();
  }

  open() {
    this.drawerTarget.classList.remove("-translate-x-full");
    this.drawerTarget.inert = false;
    this.backdropTarget.dataset.active = "";
    this._syncChrome(true);
    // Keyboard users land in the nav they just opened, not the dimmed page.
    this.drawerTarget.querySelector("a")?.focus();
  }

  close() {
    if (!this._isOpen) return;
    const focusWasInside = this.drawerTarget.contains(document.activeElement);
    this.drawerTarget.classList.add("-translate-x-full");
    this.drawerTarget.inert = !this.mediaQuery.matches;
    delete this.backdropTarget.dataset.active;
    this._syncChrome(false);
    // Hand focus back to the hamburger rather than dropping it on <body>.
    if (focusWasInside && this.hasToggleTarget) this.toggleTarget.focus();
  }

  get _isOpen() {
    return this.hasDrawerTarget && !this.drawerTarget.classList.contains("-translate-x-full");
  }

  _syncChrome(open) {
    if (this.hasToggleTarget) {
      this.toggleTarget.setAttribute("aria-expanded", open.toString());
    }
    if (this.hasOpenIconTarget && this.hasCloseIconTarget) {
      this.openIconTarget.classList.toggle("hidden", open);
      this.closeIconTarget.classList.toggle("hidden", !open);
    }
  }
}
